import { Divider, Form, message, Modal, Select, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { TicketCreationFields } from 'features/libra/dashboard/TicketCreationFields'
import type { TicketCreationFormValues } from 'features/libra/dashboard/useTicketCreationFields'
import { useTicketCreationFields } from 'features/libra/dashboard/useTicketCreationFields'
import { entryFromLibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useLibraWorkflowSteps } from 'features/libra/workflow/workflowStepsApi'
import { PermissionsGate } from 'features/permissions'
import { generateEntrySlug, useRolodexEntries } from 'features/rolodex'
import debounce from 'lodash/debounce'
import first from 'lodash/first'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  GeckoUnit,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryType,
} from 'types'
import { v4 as uuid } from 'uuid'

const { Text } = Typography

interface CreateTicketsForGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  organizationId: string
  groupId: string
  groupTitle: string
  /**
   * Allowed workflows for create. Empty/absent → all org workflows.
   * Length 1 → picker hidden; length > 1 → picker limited to these.
   */
  createWorkflowIds?: ReadonlyArray<string>
}

export const CreateTicketsForGroupModal: FC<
  CreateTicketsForGroupModalProps
> = ({
  isOpen,
  onClose,
  onSuccess,
  organizationId,
  groupId,
  groupTitle,
  createWorkflowIds,
}) => {
  const [form] = Form.useForm<TicketCreationFormValues>()
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const { rolodexPut } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()

  const allowedWorkflowIds = useMemo(
    () => createWorkflowIds?.filter(Boolean) ?? [],
    [createWorkflowIds]
  )
  const lockedWorkflowId =
    allowedWorkflowIds.length === 1 ? allowedWorkflowIds[0] : undefined
  const workflowScope = allowedWorkflowIds.join(',')

  const { libraWorkflows, isLoading: areWorkflowsLoading } =
    useLibraWorkflowSteps(
      useMemo(() => {
        if (lockedWorkflowId) {
          return { ids: [lockedWorkflowId] }
        }
        if (allowedWorkflowIds.length > 0) {
          return { ids: [...allowedWorkflowIds] }
        }
        return { tags: { organization: organizationId } }
      }, [lockedWorkflowId, allowedWorkflowIds, organizationId])
    )

  const watchedWorkflowId = Form.useWatch('workflowId', form)
  const workflowId = lockedWorkflowId ?? watchedWorkflowId
  const selectedWorkflow = libraWorkflows.find(
    (workflow) => workflow.id === workflowId
  )

  const unitsQuery = useUnitQuery({ searchText, organizationId })

  const { units: latestUnits, isLoading: areUnitsLoading } =
    useRolodexEntries(unitsQuery)

  const unitLookupRef = useRef<Map<string, GeckoUnit>>(new Map())
  const unitLookupOrganizationRef = useRef(organizationId)
  const unitMap = useMemo(() => {
    const map = unitLookupRef.current
    if (unitLookupOrganizationRef.current !== organizationId) {
      map.clear()
      unitLookupOrganizationRef.current = organizationId
    }
    latestUnits.forEach((unit) => map.set(unit.id, unit))

    return map
  }, [latestUnits, organizationId])

  const { reset: resetTicketCreationFields, ...ticketCreationFields } =
    useTicketCreationFields({
      form,
      isOpen,
      organizationId,
      groupId,
      selectedWorkflow,
      unitMap,
    })

  const { workflowOptions, unitOptions } = useMemo(() => {
    return {
      workflowOptions: libraWorkflows.map((workflow) => ({
        label: workflow.name,
        value: workflow.id,
      })),
      unitOptions: latestUnits.map((unit) => ({
        label: unit.data.name || unit.id,
        value: unit.id,
      })),
    }
  }, [libraWorkflows, latestUnits])

  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchText(value)
    }, 300)
  ).current

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  const resetModal = useCallback(() => {
    form.resetFields()
    setSearchText('')
    resetTicketCreationFields()
  }, [form, resetTicketCreationFields])

  useEffect(() => {
    resetModal()
  }, [
    groupId,
    isOpen,
    lockedWorkflowId,
    organizationId,
    resetModal,
    workflowScope,
  ])

  const handleSubmit = async (values: TicketCreationFormValues) => {
    setLoading(true)
    try {
      const workflowId = lockedWorkflowId ?? values.workflowId
      if (
        allowedWorkflowIds.length > 0 &&
        !allowedWorkflowIds.includes(workflowId)
      ) {
        messageApi.error('Selected workflow is not allowed for this group')
        return
      }

      const selectedWorkflow = libraWorkflows.find((w) => w.id === workflowId)

      if (!selectedWorkflow) {
        messageApi.error('Selected workflow not found')
        return
      }

      const defaultStepId = first(selectedWorkflow.steps)?.id ?? ''

      const initialFieldsByUnit = ticketCreationFields.prepareInitialFields(
        values,
        selectedWorkflow
      )
      if (!initialFieldsByUnit) return

      const ticketsToCreate = values.unitIds.map((unitId) => {
        const unit = unitMap.get(unitId)
        const ticketId = generateEntrySlug('ticket', uuid().slice(0, 6))
        const workflowName = selectedWorkflow.name
        const unitName = unit?.data.name || unitId

        const lifecycleDefault = selectedWorkflow.lifecycleConfig
          ? (selectedWorkflow.lifecycleConfig.stages.find((s) => s.default)
              ?.id ??
            selectedWorkflow.lifecycleConfig.stages[0]?.id ??
            '')
          : ''

        return entryFromLibraTicket({
          id: ticketId,
          title: `${workflowName} - ${unitName}`,
          fieldValues: initialFieldsByUnit.get(unitId) ?? {},
          stepId: defaultStepId,
          workflowComplete: false,
          version: 1,
          assessmentId: uuid(),
          completedBy: null,
          createdBy: null,
          lastUpdatedBy: null,
          ticketStatus: lifecycleDefault,
          terminalTransitionBy: null,
          terminalTransitionAt: null,
          submittedForReviewBy: null,
          submittedForReviewAt: null,
          contributors: [],
          inputProvenance: {},
          workflowId,
          tags: {
            organization: organizationId,
            site: unit?.tags.site || '',
            unit: unitId,
            [RolodexEntryType.LIBRA_WORKFLOW]: workflowId,
            [RolodexEntryType.LIBRA_TICKET_GROUP]: groupId,
          },
        })
      })

      await rolodexPut(ticketsToCreate)
      messageApi.success(
        `${ticketsToCreate.length} ticket${ticketsToCreate.length > 1 ? 's' : ''} created successfully!`
      )

      resetModal()
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error creating tickets:', error)
      messageApi.error('Failed to create tickets. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    resetModal()
    onClose()
  }

  return (
    <>
      {contextHolder}
      <Modal
        title={`Create Tickets for "${groupTitle}"`}
        open={isOpen}
        onOk={form.submit}
        onCancel={handleCancel}
        confirmLoading={loading}
        okButtonProps={{
          disabled:
            loading || areWorkflowsLoading || !ticketCreationFields.isReady,
        }}
        okText='Create Tickets'
        cancelText='Cancel'
        width={700}>
        <Form form={form} layout='vertical' onFinish={handleSubmit}>
          {lockedWorkflowId ? (
            <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
              <Form.Item label='Workflow Template'>
                <Text>
                  {areWorkflowsLoading
                    ? 'Loading…'
                    : (selectedWorkflow?.name ?? lockedWorkflowId)}
                </Text>
              </Form.Item>
              <Divider />
            </PermissionsGate>
          ) : (
            <>
              <Form.Item
                name='workflowId'
                label='Workflow Template'
                rules={[
                  { required: true, message: 'Please select a workflow' },
                ]}>
                <Select
                  showSearch
                  placeholder='Select workflow'
                  loading={areWorkflowsLoading}
                  options={workflowOptions}
                  optionFilterProp='label'
                  onChange={resetTicketCreationFields}
                />
              </Form.Item>
              <Divider />
            </>
          )}

          <Form.Item
            name='unitIds'
            label={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}>
                <span>Units/Assets</span>
                <Text
                  type='secondary'
                  style={{ fontSize: '12px', fontWeight: 'normal' }}>
                  {searchText.trim().length < 2
                    ? `Showing first ${latestUnits.length} units. Type to search for specific units.`
                    : `Found ${latestUnits.length} unit${latestUnits.length !== 1 ? 's' : ''} matching "${searchText}"`}
                </Text>
              </div>
            }
            rules={[
              { required: true, message: 'Please select at least one unit' },
            ]}>
            <Select
              mode='multiple'
              showSearch
              onSearch={handleSearch}
              onChange={resetTicketCreationFields}
              placeholder='Type to search units...'
              loading={areUnitsLoading}
              filterOption={false}
              options={unitOptions}
              notFoundContent={
                areUnitsLoading
                  ? 'Searching...'
                  : searchText.trim().length < 2
                    ? 'Type at least 2 characters to search'
                    : 'No units found'
              }
              maxTagCount='responsive'
            />
          </Form.Item>
          <TicketCreationFields {...ticketCreationFields.sectionProps} />
        </Form>
      </Modal>
    </>
  )
}

const useUnitQuery = ({
  searchText,
  organizationId,
}: {
  searchText: string
  organizationId: string
}) => {
  return useMemo(() => {
    const baseQuery = {
      type: RolodexEntryType.UNIT,
      tags: { organization: organizationId },
    }

    if (!searchText || searchText.trim().length < 2) {
      return {
        ...baseQuery,
        limit: 30,
        order_by: [
          {
            key: 'name',
            descending: false,
          },
        ],
      }
    }

    return {
      ...baseQuery,
      filters: [
        {
          key: 'name',
          value: searchText.trim(),
        },
      ],
      limit: 100,
    }
  }, [searchText, organizationId])
}
